/**
 * 3Cs / feel contract (Epic B + design §15). `three-cs.json` is a discriminated union by
 * archetype — side-scroll auto-runner (Bumble) or top-down action-adventure (StarTropics-
 * like). Source of truth for level validation + generation; the archetype also decides
 * which level kind "+ New level" creates. Validated by the real crucible-levels schemas.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  ThreeCsProfileSchema,
  type ThreeCsProfile,
  type AutorunnerThreeCs,
  type TopdownThreeCs,
  type DataThreeCs,
} from '@volley/crucible-levels';
import { readThreeCs, writeThreeCs } from './anvil-api';
import { useCrucibleClient } from '../../transport/context.js';
import { DEFAULT_THREE_CS, DEFAULT_TOPDOWN_THREE_CS, DEFAULT_DATA_THREE_CS } from './LevelEditor';

export interface ThreeCsState {
  profile: ThreeCsProfile;
  loaded: boolean;
  /** True once an authored three-cs.json has been read (vs the built-in default). */
  authored: boolean;
  /** True when the unauthored default was derived from the design (AI archetype judge). */
  derived: boolean;
  save: (profile: ThreeCsProfile) => Promise<void>;
  /** Swap the unauthored default (archetype derived from the design). No-op once authored. */
  applyDerived: (profile: ThreeCsProfile) => void;
}

export function useThreeCs(gameId: string): ThreeCsState {
  const client = useCrucibleClient();
  const [profile, setProfile] = useState<ThreeCsProfile>(DEFAULT_THREE_CS);
  const [loaded, setLoaded] = useState(false);
  const [authored, setAuthored] = useState(false);
  const [derived, setDerived] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoaded(false);
    readThreeCs(client, gameId)
      .then((raw) => {
        if (cancelled) return;
        const parsed = ThreeCsProfileSchema.safeParse(raw);
        setProfile(parsed.success ? parsed.data : DEFAULT_THREE_CS);
        setAuthored(parsed.success);
        setLoaded(true);
      })
      .catch(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [gameId, client]);

  const save = useCallback(
    async (next: ThreeCsProfile): Promise<void> => {
      await writeThreeCs(client, gameId, next);
      setProfile(next);
      setAuthored(true);
      setDerived(false);
    },
    [gameId, client],
  );

  const applyDerived = useCallback((next: ThreeCsProfile): void => {
    setAuthored((isAuthored) => {
      if (!isAuthored) {
        setProfile(next);
        setDerived(true);
      }
      return isAuthored;
    });
  }, []);

  return { profile, loaded, authored, derived, save, applyDerived };
}

function DataFields({
  draft,
  setDraft,
}: {
  draft: DataThreeCs;
  setDraft: (p: ThreeCsProfile) => void;
}): React.ReactElement {
  return (
    <div className="space-y-3">
      <p className="text-sm text-crucible-muted">
        This game is property-driven: levels are DATA blobs the game consumes directly — starting
        boards, gem loot tables, grab-bag sources. No kinematics to contract; each level is a typed
        row set validated by the data-table schema.
      </p>
      <label className="flex flex-col gap-1 text-sm text-crucible-fg">
        <span className="text-crucible-muted">What the data levels represent</span>
        <input
          value={draft.notes ?? ''}
          onChange={(e) => setDraft({ ...draft, notes: e.target.value || undefined })}
          placeholder="e.g. starting boards + gem loot tables"
          className="rounded border border-crucible-border bg-crucible-bg-elevated px-2 py-1 text-sm text-crucible-fg"
        />
      </label>
    </div>
  );
}

function NumberField({
  label,
  value,
  onChange,
  step = 0.1,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  step?: number;
}): React.ReactElement {
  return (
    <label className="flex items-center justify-between gap-2 text-sm text-crucible-fg">
      <span className="text-crucible-muted">{label}</span>
      <input
        type="number"
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-24 rounded border border-crucible-border bg-crucible-bg-elevated px-2 py-1 text-right text-sm text-crucible-fg"
      />
    </label>
  );
}

function AutorunnerFields({
  draft,
  setDraft,
}: {
  draft: AutorunnerThreeCs;
  setDraft: (p: ThreeCsProfile) => void;
}): React.ReactElement {
  const setChar = (k: keyof AutorunnerThreeCs['character'], v: number): void =>
    setDraft({ ...draft, character: { ...draft.character, [k]: v } });
  const tether = draft.tether;
  const setTether = (k: 'members' | 'restLength' | 'maxStretch', v: number): void => {
    if (!tether) return;
    setDraft({ ...draft, tether: { ...tether, [k]: v } });
  };
  const playerDriven = draft.locomotion === 'player';
  return (
    <>
      <fieldset className="rounded-lg border border-crucible-border p-3">
        <legend className="px-1 text-[11px] uppercase tracking-wide text-crucible-muted">
          Character · Controls
        </legend>
        <div className="grid gap-2">
          <label className="flex items-center justify-between gap-2 text-sm text-crucible-fg">
            <span className="text-crucible-muted">Locomotion</span>
            <select
              value={draft.locomotion ?? 'auto'}
              onChange={(e) =>
                setDraft({ ...draft, locomotion: e.target.value === 'player' ? 'player' : 'auto' })
              }
              className="w-40 rounded border border-crucible-border bg-crucible-bg-elevated px-2 py-1 text-sm text-crucible-fg"
            >
              <option value="auto">Auto-run (runner)</option>
              <option value="player">Player-driven (Mario-like)</option>
            </select>
          </label>
          <NumberField
            label={playerDriven ? 'Run speed (m/s)' : 'Auto-run speed (m/s)'}
            value={draft.character.autoRunSpeed}
            onChange={(v) => setChar('autoRunSpeed', v)}
          />
          <NumberField
            label="Backpedal speed (m/s)"
            value={draft.character.backpedalSpeed}
            onChange={(v) => setChar('backpedalSpeed', v)}
          />
          <NumberField
            label="Jump height (m)"
            value={draft.character.jumpHeight}
            onChange={(v) => setChar('jumpHeight', v)}
          />
          <NumberField
            label="Jump air time (s)"
            value={draft.character.jumpAirTime}
            onChange={(v) => setChar('jumpAirTime', v)}
          />
          <NumberField
            label="Downed drag penalty (0–1)"
            value={draft.character.downedDragSpeedPenalty}
            onChange={(v) => setChar('downedDragSpeedPenalty', v)}
            step={0.05}
          />
          <NumberField
            label="Body width (m)"
            value={draft.character.bodyWidth}
            onChange={(v) => setChar('bodyWidth', v)}
          />
        </div>
      </fieldset>
      <fieldset className="rounded-lg border border-crucible-border p-3">
        <legend className="px-1 text-[11px] uppercase tracking-wide text-crucible-muted">
          Tether
        </legend>
        <div className="grid gap-2">
          <label className="flex items-center justify-between gap-2 text-sm text-crucible-fg">
            <span className="text-crucible-muted">Tethered group</span>
            <input
              type="checkbox"
              checked={tether !== undefined}
              onChange={(e) =>
                setDraft(
                  e.target.checked
                    ? { ...draft, tether: { members: 4, restLength: 4, maxStretch: 8 } }
                    : { ...draft, tether: undefined },
                )
              }
            />
          </label>
          {tether ? (
            <>
              <NumberField
                label="Members"
                value={tether.members}
                onChange={(v) => setTether('members', v)}
                step={1}
              />
              <NumberField
                label="Rest length (m)"
                value={tether.restLength}
                onChange={(v) => setTether('restLength', v)}
              />
              <NumberField
                label="Max stretch (m)"
                value={tether.maxStretch}
                onChange={(v) => setTether('maxStretch', v)}
              />
            </>
          ) : (
            <p className="text-xs text-crucible-muted">
              Untethered — solo/independent movement; group-solvability checks are skipped.
            </p>
          )}
        </div>
      </fieldset>
    </>
  );
}

function TopdownFields({
  draft,
  setDraft,
}: {
  draft: TopdownThreeCs;
  setDraft: (p: ThreeCsProfile) => void;
}): React.ReactElement {
  const setChar = (k: keyof TopdownThreeCs['character'], v: number): void =>
    setDraft({ ...draft, character: { ...draft.character, [k]: v } });
  const setCam = (k: keyof TopdownThreeCs['camera'], v: number): void =>
    setDraft({ ...draft, camera: { ...draft.camera, [k]: v } });
  return (
    <>
      <fieldset className="rounded-lg border border-crucible-border p-3">
        <legend className="px-1 text-[11px] uppercase tracking-wide text-crucible-muted">
          Character · Controls
        </legend>
        <div className="grid gap-2">
          <NumberField
            label="Move speed (m/s)"
            value={draft.character.moveSpeed}
            onChange={(v) => setChar('moveSpeed', v)}
          />
          <NumberField
            label="Acceleration (m/s²)"
            value={draft.character.accel}
            onChange={(v) => setChar('accel', v)}
            step={1}
          />
          <NumberField
            label="Body radius (m)"
            value={draft.character.bodyRadius}
            onChange={(v) => setChar('bodyRadius', v)}
            step={0.05}
          />
        </div>
      </fieldset>
      <fieldset className="rounded-lg border border-crucible-border p-3">
        <legend className="px-1 text-[11px] uppercase tracking-wide text-crucible-muted">
          Camera
        </legend>
        <div className="grid gap-2">
          <NumberField
            label="View width (m)"
            value={draft.camera.viewW}
            onChange={(v) => setCam('viewW', v)}
            step={1}
          />
          <NumberField
            label="View height (m)"
            value={draft.camera.viewH}
            onChange={(v) => setCam('viewH', v)}
            step={1}
          />
        </div>
      </fieldset>
    </>
  );
}

export function ThreeCsEditor({
  profile,
  authored,
  derived = false,
  onSave,
}: {
  profile: ThreeCsProfile;
  authored: boolean;
  /** The unauthored default's archetype was derived from the design. */
  derived?: boolean;
  onSave: (profile: ThreeCsProfile) => void;
}): React.ReactElement {
  const [draft, setDraft] = useState<ThreeCsProfile>(profile);
  useEffect(() => setDraft(profile), [profile]);

  const check = ThreeCsProfileSchema.safeParse(draft);

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4">
      <div className="flex items-center gap-2">
        <h2 className="text-base font-medium text-crucible-fg">Feel · 3Cs</h2>
        <span className="rounded-full border border-crucible-border px-2 py-0.5 text-[11px] text-crucible-muted">
          {authored ? 'authored' : derived ? 'derived from design (unsaved)' : 'default (unsaved)'}
        </span>
        <button
          type="button"
          onClick={() => check.success && onSave(check.data)}
          disabled={!check.success}
          className="ml-auto rounded border border-crucible-border px-2 py-1 text-sm text-crucible-fg hover:bg-crucible-bg-elevated disabled:opacity-50"
        >
          Save
        </button>
      </div>
      <p className="text-xs text-crucible-muted">
        The measurable feel contract that drives level validation + generation. The archetype
        decides what kind of level this game builds (side-scroll track vs top-down floor plan).
      </p>

      <label className="flex items-center justify-between gap-2 text-sm text-crucible-fg">
        <span className="text-crucible-muted">Level type</span>
        <select
          value={draft.archetype}
          onChange={(e) =>
            setDraft(
              e.target.value === 'topdown-2d'
                ? DEFAULT_TOPDOWN_THREE_CS
                : e.target.value === 'data-table'
                  ? DEFAULT_DATA_THREE_CS
                  : DEFAULT_THREE_CS,
            )
          }
          className="w-56 rounded border border-crucible-border bg-crucible-bg-elevated px-2 py-1 text-sm text-crucible-fg"
        >
          <option value="autorunner-2d">Side-scroll auto-runner</option>
          <option value="topdown-2d">Top-down action-adventure</option>
          <option value="data-table">Data (property-driven)</option>
        </select>
      </label>

      {draft.archetype === 'autorunner-2d' ? (
        <AutorunnerFields draft={draft} setDraft={setDraft} />
      ) : draft.archetype === 'topdown-2d' ? (
        <TopdownFields draft={draft} setDraft={setDraft} />
      ) : (
        <DataFields draft={draft} setDraft={setDraft} />
      )}

      {!check.success && (
        <p className="text-xs text-red-400">
          {check.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`).join('; ')}
        </p>
      )}
    </div>
  );
}
